import { useState, useEffect, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Users, MessageSquare, Presentation, Calendar, BarChart3, Calculator,
  Network, FileText, Link2, Search, Plus, Download, Loader2,
  Copy, ArrowRight, ArrowLeft, Sparkles, CheckCircle, ChevronDown,
  ChevronUp, ExternalLink, Globe, Eye, Trash2, X
} from "lucide-react";

const TABS = [
  { key: "leads", label: "Leads", icon: Users },
  { key: "demolinks", label: "Demo Link", icon: Link2 },
  { key: "feedback", label: "Demo Feedback", icon: MessageSquare },
  { key: "bookings", label: "Bookings", icon: Calendar },
  { key: "audit", label: "Audit Log", icon: FileText },
  { key: "assessments", label: "Maturity Assessment", icon: BarChart3 },
  { key: "codn", label: "CODN Calculator", icon: Calculator },
  { key: "mapper", label: "System Mapper", icon: Network },
  { key: "presentations", label: "Presentation Generator", icon: Presentation },
];

export default function PreSalesHub() {
  const [activeTab, setActiveTab] = useState("leads");
  const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
  const navigate = useNavigate();

  // Redirect external users
  useEffect(() => {
    if (userData.user_type === "external" && userData.role !== "admin") {
      navigate("/", { replace: true });
    }
  }, []);
  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="pt-16 md:pt-24 pb-8 md:pb-12">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6">
          {/* Header */}
          <div className="mb-4 md:mb-6">
            <h1 className="text-xl md:text-2xl font-bold text-slate-900" data-testid="presales-title">Pre-Sales Hub</h1>
            <p className="text-xs md:text-sm text-slate-500 mt-0.5">Sales enablement tools for prospect alignment and conversion</p>
          </div>

          {/* Tabs — horizontal scroll on mobile */}
          <div className="flex gap-1 overflow-x-auto pb-2 mb-4 md:mb-6 -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide" data-testid="presales-tabs">
            {TABS.map((tab) => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs md:text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.key
                    ? "bg-[#0066CC] text-white shadow-sm"
                    : "text-slate-600 hover:bg-slate-200"
                }`} data-testid={`tab-${tab.key}`}>
                <tab.icon className="w-3.5 h-3.5 md:w-4 md:h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="bg-white rounded-xl md:rounded-2xl border border-slate-200 shadow-sm min-h-[400px] md:min-h-[500px]">
            {activeTab === "leads" && <LeadsTab />}
            {activeTab === "feedback" && <FeedbackTab />}
            {activeTab === "presentations" && <PresentationTab />}
            {activeTab === "bookings" && <BookingsTab />}
            {activeTab === "assessments" && <AssessmentsTab />}
            {activeTab === "codn" && <CODNTab />}
            {activeTab === "mapper" && <MapperTab />}
            {activeTab === "audit" && <AuditTab />}
            {activeTab === "demolinks" && <DemoLinksTab />}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

/* ============ LEADS TAB ============ */
function LeadsTab() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", vertical_interest: "", message: "" });
  const [saving, setSaving] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [analyticsMap, setAnalyticsMap] = useState({});
  const [expandedLead, setExpandedLead] = useState(null);

  useEffect(() => { loadLeads(); loadAnalytics(); }, []);
  const loadLeads = async () => { setLoading(true); try { const r = await api.get("/presales/leads?status=all"); setLeads(r.data || []); } catch {} setLoading(false); };
  const loadAnalytics = async () => {
    try {
      const r = await api.get("/presales/lead-analytics");
      const map = {};
      (r.data || []).forEach(a => { map[a.email?.toLowerCase()] = a; });
      setAnalyticsMap(map);
    } catch {}
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Name and email required"); return; }
    setSaving(true);
    try {
      const r = await api.post("/leads/internal-create", form);
      toast.success("Lead created & portal account ready!");
      setShowCreate(false);
      setForm({ name: "", email: "", phone: "", company: "", vertical_interest: "", message: "" });
      if (r.data?.user_credentials) {
        setCredentials(r.data.user_credentials);
      }
      loadLeads();
      loadAnalytics();
    } catch { toast.error("Failed to create lead"); }
    setSaving(false);
  };

  const toggleActive = async (lead) => {
    const newActive = lead.is_active === false ? true : false;
    setTogglingId(lead.id);
    try {
      await api.put(`/presales/leads/${lead.id}/toggle-active`, { is_active: newActive });
      toast.success(newActive ? "Lead activated" : "Lead deactivated — portal access revoked");
      loadLeads();
    } catch { toast.error("Failed to update status"); }
    setTogglingId(null);
  };

  const copyToClipboard = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copied!`); }
    catch { toast.success("Copied!"); }
  };

  const filtered = leads.filter((l) => {
    const q = search.toLowerCase();
    return !q || (l.name || "").toLowerCase().includes(q) || (l.email || "").toLowerCase().includes(q) || (l.company || "").toLowerCase().includes(q);
  });

  const TempBadge = ({ temp }) => {
    const map = {
      hot: "bg-red-100 text-red-700 border-red-200",
      warm: "bg-amber-100 text-amber-700 border-amber-200",
      cold: "bg-blue-100 text-blue-700 border-blue-200",
      new: "bg-slate-100 text-slate-500 border-slate-200",
    };
    const labels = { hot: "Hot", warm: "Warm", cold: "Cold", new: "New" };
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border ${map[temp] || map.new}`}>{labels[temp] || "New"}</span>;
  };

  const LeadInsightPanel = ({ a }) => (
    <div className="bg-gradient-to-r from-slate-50 to-blue-50/30 border border-slate-200 rounded-xl p-4 mt-1 mb-2 animate-in slide-in-from-top-2" data-testid={`insight-panel-${a.email}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-[#0066CC]" />
          <span className="text-sm font-semibold text-slate-800">Lead Engagement Insights</span>
        </div>
        <TempBadge temp={a.temperature} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <div className="bg-white rounded-lg p-2.5 text-center border border-slate-100">
          <p className="text-lg font-bold text-slate-800">{a.total_time_display}</p>
          <p className="text-[10px] text-slate-500 font-medium uppercase">Time Spent</p>
        </div>
        <div className="bg-white rounded-lg p-2.5 text-center border border-slate-100">
          <p className="text-lg font-bold text-slate-800">{a.total_sessions}</p>
          <p className="text-[10px] text-slate-500 font-medium uppercase">Sessions</p>
        </div>
        <div className="bg-white rounded-lg p-2.5 text-center border border-slate-100">
          <p className="text-lg font-bold text-slate-800">{a.pages_visited}</p>
          <p className="text-[10px] text-slate-500 font-medium uppercase">Pages</p>
        </div>
        <div className="bg-white rounded-lg p-2.5 text-center border border-slate-100">
          <p className="text-lg font-bold text-slate-800">{a.problems_viewed}</p>
          <p className="text-[10px] text-slate-500 font-medium uppercase">Problems</p>
        </div>
        <div className="bg-white rounded-lg p-2.5 text-center border border-slate-100">
          <p className="text-lg font-bold text-slate-800">{a.ai_questions}</p>
          <p className="text-[10px] text-slate-500 font-medium uppercase">AI Queries</p>
        </div>
      </div>
      {(a.industries_accessed?.length > 0 || a.last_active) && (
        <div className="flex flex-wrap items-center gap-3 mt-2.5 text-xs text-slate-400">
          {a.industries_accessed?.length > 0 && <span>Industries: <span className="text-slate-600 font-medium">{a.industries_accessed.join(", ")}</span></span>}
          {a.last_active && <span className="ml-auto">Last active: <span className="text-slate-600">{new Date(a.last_active).toLocaleDateString()}</span></span>}
        </div>
      )}
    </div>
  );

  const NoInsightPanel = () => (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mt-1 mb-2 text-center">
      <p className="text-sm text-slate-400">No portal activity recorded for this lead yet.</p>
    </div>
  );

  return (
    <div className="p-4 md:p-5">
      {/* Credential Card - shown after creating a lead */}
      {credentials && (
        <div className="mb-4 bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="lead-credentials-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              <h3 className="font-semibold text-emerald-900 text-sm">Portal Account Created</h3>
            </div>
            <button onClick={() => setCredentials(null)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
          </div>
          <p className="text-xs text-emerald-700 mb-3">Share these credentials with the lead so they can access the portal:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="bg-white rounded-lg border border-emerald-100 p-3">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Username</p>
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-sm text-slate-800 truncate">{credentials.username}</span>
                <button onClick={() => copyToClipboard(credentials.username, "Username")}
                  className="shrink-0 p-1.5 rounded-md bg-emerald-100 hover:bg-emerald-200 text-emerald-700 transition-colors" data-testid="copy-username">
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <div className="bg-white rounded-lg border border-emerald-100 p-3">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Password</p>
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-sm text-slate-800 truncate">{credentials.password}</span>
                <button onClick={() => copyToClipboard(credentials.password, "Password")}
                  className="shrink-0 p-1.5 rounded-md bg-emerald-100 hover:bg-emerald-200 text-emerald-700 transition-colors" data-testid="copy-password">
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
          <button onClick={() => {
            const text = `Portal Credentials\nUsername: ${credentials.username}\nPassword: ${credentials.password}\n\nLogin at: ${window.location.origin}/login`;
            copyToClipboard(text, "All credentials");
          }} className="mt-3 flex items-center gap-1.5 text-xs text-emerald-700 font-medium hover:text-emerald-900 transition-colors" data-testid="copy-all-creds">
            <Copy className="w-3 h-3" /> Copy all with login link
          </button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="flex items-center gap-3 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search leads..." className="pl-9" data-testid="leads-search" />
          </div>
          <span className="text-xs md:text-sm text-slate-400 whitespace-nowrap">{filtered.length} leads</span>
        </div>
        <Button onClick={() => setShowCreate(!showCreate)} className="bg-[#0066CC] hover:bg-[#0052A3] gap-1 w-full sm:w-auto" data-testid="create-lead-btn">
          <Plus className="w-4 h-4" /> New Lead
        </Button>
      </div>

      {showCreate && (
        <form onSubmit={handleCreate} className="mb-4 p-3 md:p-4 bg-blue-50 rounded-xl border border-blue-100 space-y-3" data-testid="lead-create-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
            <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Name *" required className="text-sm" />
            <Input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="Email *" type="email" required className="text-sm" />
            <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="Phone" className="text-sm" />
            <Input value={form.company} onChange={(e) => setForm({...form, company: e.target.value})} placeholder="Company" className="text-sm" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input value={form.vertical_interest} onChange={(e) => setForm({...form, vertical_interest: e.target.value})} placeholder="Industry / Vertical" className="text-sm" />
            <div className="flex gap-2">
              <Input value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} placeholder="Notes" className="text-sm flex-1" />
              <Button type="submit" disabled={saving} size="sm" className="bg-[#0066CC]">{saving ? "..." : "Create"}</Button>
              <Button type="button" variant="outline" size="sm" onClick={() => setShowCreate(false)}>Cancel</Button>
            </div>
          </div>
        </form>
      )}

      {loading ? <LoadingState /> : filtered.length === 0 ? <EmptyState icon={Users} text="No leads found" /> : (
        <>
          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 border-b">
                <th className="text-left px-3 py-2 font-semibold text-slate-600">Name</th>
                <th className="text-left px-3 py-2 font-semibold text-slate-600">Email</th>
                <th className="text-left px-3 py-2 font-semibold text-slate-600">Company</th>
                <th className="text-left px-3 py-2 font-semibold text-slate-600">Industry</th>
                <th className="text-left px-3 py-2 font-semibold text-slate-600">Status</th>
                <th className="text-center px-3 py-2 font-semibold text-slate-600">Temp</th>
                <th className="text-left px-3 py-2 font-semibold text-slate-600">Active</th>
                <th className="text-left px-3 py-2 font-semibold text-slate-600">Date</th>
                <th className="text-center px-3 py-2 font-semibold text-slate-600">Insights</th>
              </tr></thead>
              <tbody>
                {filtered.slice(0, 50).map((l) => {
                  const a = analyticsMap[l.email?.toLowerCase()];
                  const isExpanded = expandedLead === l.id;
                  return (
                    <Fragment key={l.id}>
                      <tr className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${l.is_active === false ? "opacity-50" : ""}`}>
                        <td className="px-3 py-2.5 font-medium text-slate-900">{l.name}</td>
                        <td className="px-3 py-2.5 text-slate-500 text-xs">{l.email}</td>
                        <td className="px-3 py-2.5 text-slate-600">{l.company || "—"}</td>
                        <td className="px-3 py-2.5 text-slate-500 text-xs">{l.vertical_interest || "—"}</td>
                        <td className="px-3 py-2.5"><span className={`px-2 py-0.5 rounded text-xs font-medium ${l.status === "new" ? "bg-blue-100 text-blue-700" : l.status === "contacted" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{l.status}</span></td>
                        <td className="px-3 py-2.5 text-center"><TempBadge temp={a?.temperature || "new"} /></td>
                        <td className="px-3 py-2.5">
                          <button onClick={() => toggleActive(l)} disabled={togglingId === l.id}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${l.is_active === false ? "bg-slate-300" : "bg-emerald-500"}`}
                            data-testid={`toggle-active-${l.id}`}>
                            <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform ${l.is_active === false ? "translate-x-0.5" : "translate-x-[18px]"}`} />
                          </button>
                        </td>
                        <td className="px-3 py-2.5 text-xs text-slate-400">{l.created_at ? new Date(l.created_at).toLocaleDateString() : "—"}</td>
                        <td className="px-3 py-2.5 text-center">
                          <button onClick={() => setExpandedLead(isExpanded ? null : l.id)}
                            className={`p-1.5 rounded-lg transition-colors ${isExpanded ? "bg-[#0066CC] text-white" : "hover:bg-slate-100 text-slate-400 hover:text-[#0066CC]"}`}
                            data-testid={`lead-insight-btn-${l.id}`}>
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr><td colSpan={9} className="px-3 py-0">
                          {a ? <LeadInsightPanel a={a} /> : <NoInsightPanel />}
                        </td></tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Mobile Cards */}
          <div className="md:hidden space-y-2">
            {filtered.slice(0, 50).map((l) => {
              const a = analyticsMap[l.email?.toLowerCase()];
              const isExpanded = expandedLead === l.id;
              return (
                <div key={l.id} className={`bg-white rounded-xl border border-slate-200 ${l.is_active === false ? "opacity-50" : ""}`}>
                  <div className="p-3">
                    <div className="flex items-center justify-between mb-1">
                      <p className="font-medium text-sm text-slate-900 truncate flex-1">{l.name}</p>
                      <div className="flex items-center gap-2 ml-2 shrink-0">
                        <TempBadge temp={a?.temperature || "new"} />
                        <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${l.status === "new" ? "bg-blue-100 text-blue-700" : l.status === "contacted" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{l.status}</span>
                        <button onClick={() => toggleActive(l)} disabled={togglingId === l.id}
                          className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${l.is_active === false ? "bg-slate-300" : "bg-emerald-500"}`}>
                          <span className={`inline-block h-2.5 w-2.5 rounded-full bg-white shadow-sm transition-transform ${l.is_active === false ? "translate-x-0.5" : "translate-x-[14px]"}`} />
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 truncate">{l.email}</p>
                    <div className="flex items-center justify-between mt-1.5">
                      <p className="text-xs text-slate-400 truncate">{l.company || "—"} {l.vertical_interest ? `· ${l.vertical_interest}` : ""}</p>
                      <div className="flex items-center gap-2 shrink-0">
                        <p className="text-[10px] text-slate-400">{l.created_at ? new Date(l.created_at).toLocaleDateString() : "—"}</p>
                        <button onClick={() => setExpandedLead(isExpanded ? null : l.id)}
                          className={`p-1 rounded-md transition-colors ${isExpanded ? "bg-[#0066CC] text-white" : "text-slate-400 hover:text-[#0066CC]"}`}>
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="px-3 pb-3">
                      {a ? <LeadInsightPanel a={a} /> : <NoInsightPanel />}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

/* ============ FEEDBACK TAB — Customer Feedback Management ============ */
const CUSTOMER_Q_LABELS = {
  q1: "Demo Experience", q2: "Relevance", q3: "Buying Intent",
  q4: "Timeline", q5: "Blockers?", q5_blockers: "Blocker Details",
  q5_other: "Other Details", q6: "Consultant Rating", q7: "Next Step", q7_details: "Topic",
};
const CUSTOMER_RATING_KEYS = ["q1", "q6"];

const RatingBadge = ({ value }) => {
  const num = parseInt(value);
  if (isNaN(num)) return <span className="text-slate-400 text-sm">-</span>;
  const colors = ["", "bg-red-100 text-red-700", "bg-orange-100 text-orange-700", "bg-amber-100 text-amber-700", "bg-emerald-100 text-emerald-700", "bg-green-100 text-green-700"];
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-bold ${colors[num] || "bg-slate-100"}`}>{num}/5</span>;
};

const IntentBadge = ({ intent }) => {
  const map = { hot: "bg-red-100 text-red-700", warm: "bg-amber-100 text-amber-700", nurture: "bg-blue-100 text-blue-700", cool: "bg-slate-100 text-slate-600" };
  return <span className={`px-2 py-0.5 rounded-md text-xs font-bold uppercase ${map[intent] || "bg-slate-100 text-slate-600"}`}>{intent || "-"}</span>;
};

function FeedbackTab() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [campaignResponses, setCampaignResponses] = useState({});
  const [viewFeedback, setViewFeedback] = useState(null);
  const [linkModal, setLinkModal] = useState(null);
  const [newTitle, setNewTitle] = useState("");
  const [allUsers, setAllUsers] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [creating, setCreating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);

  useEffect(() => { loadCampaigns(); }, []);

  const loadCampaigns = async () => {
    setLoading(true);
    try { const r = await api.get("/presales/feedback/campaigns"); setCampaigns(r.data || []); }
    catch { toast.error("Failed to load campaigns"); }
    setLoading(false);
  };

  const loadUsers = async () => {
    try { const r = await api.get("/presales/feedback/external-users"); setAllUsers(r.data || []); }
    catch {}
  };

  const handleOpenCreate = async () => { setShowCreate(true); setNewTitle(""); setSelectedEmails([]); setUserSearch(""); await loadUsers(); };
  const toggleEmail = (email) => setSelectedEmails(prev => prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]);
  const selectAll = () => { if (selectedEmails.length === allUsers.length) setSelectedEmails([]); else setSelectedEmails(allUsers.map(u => u.email)); };

  const handleCreate = async () => {
    if (!newTitle.trim()) { toast.error("Title is required"); return; }
    if (selectedEmails.length === 0) { toast.error("Select at least one user"); return; }
    setCreating(true);
    try {
      const baseUrl = window.location.origin;
      const res = await api.post("/presales/feedback/campaigns", { title: newTitle, selected_users: selectedEmails, base_url: baseUrl });
      toast.success(res.data.message);
      setShowCreate(false);
      loadCampaigns();
      const feedbackUrl = res.data.feedback_url || `${baseUrl}/feedback/${res.data.token}`;
      const users = selectedEmails.map(email => { const u = allUsers.find(usr => usr.email === email); return { email, name: u?.name || email.split("@")[0] }; });
      setLinkModal({ url: feedbackUrl, title: newTitle, token: res.data.token, users });
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create"); }
    setCreating(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this campaign and all its responses?")) return;
    try { await api.delete(`/presales/feedback/campaigns/${id}`); toast.success("Deleted"); loadCampaigns(); }
    catch { toast.error("Failed"); }
  };

  const handleExpand = async (campaignId) => {
    if (expandedCampaign === campaignId) { setExpandedCampaign(null); return; }
    setExpandedCampaign(campaignId);
    if (!campaignResponses[campaignId]) {
      try {
        const res = await api.get(`/presales/feedback/campaigns/${campaignId}/responses`);
        setCampaignResponses(prev => ({ ...prev, [campaignId]: res.data }));
      } catch { toast.error("Failed to load responses"); }
    }
  };

  const handleExport = async (campaignId) => {
    setExporting(true);
    try {
      const res = await api.get(`/presales/feedback/campaigns/${campaignId}/export/excel`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a"); link.href = url;
      link.download = `Customer_Feedback_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
      toast.success("Exported");
    } catch { toast.error("Export failed"); }
    setExporting(false);
  };

  const safeCopy = async (text) => {
    try { await navigator.clipboard.writeText(text); return true; }
    catch { const ta = document.createElement("textarea"); ta.value = text; ta.style.cssText = "position:fixed;left:-9999px"; document.body.appendChild(ta); ta.select(); try { document.execCommand("copy"); document.body.removeChild(ta); return true; } catch { document.body.removeChild(ta); return false; } }
  };

  const copyLink = (token, campaign) => {
    const url = `${window.location.origin}/feedback/${token}`;
    const users = (campaign?.users || []).map(u => ({ email: u.email, name: u.name || u.email.split("@")[0] }));
    setLinkModal({ url, title: campaign?.title || "Feedback", token, users });
  };

  const handleCopyUrl = async (url) => { const ok = await safeCopy(url); if (ok) toast.success("Link copied"); else toast.error("Could not copy"); };

  const deleteResponse = async (feedbackId, campaignId) => {
    if (!window.confirm("Delete this response?")) return;
    try {
      await api.delete(`/presales/feedback/responses/${feedbackId}`);
      toast.success("Response deleted");
      const res = await api.get(`/presales/feedback/campaigns/${campaignId}/responses`);
      setCampaignResponses(prev => ({ ...prev, [campaignId]: res.data }));
      loadCampaigns();
    } catch { toast.error("Failed"); }
  };

  const filteredUsers = allUsers.filter(u => {
    if (selectedEmails.includes(u.email)) return false;
    const q = userSearch.toLowerCase().trim();
    if (!q) return true;
    return (u.name || "").toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
  });

  return (
    <div className="p-4 md:p-5 space-y-4" data-testid="feedback-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-base md:text-lg font-semibold text-slate-900">Demo Feedback</h2>
          <p className="text-xs text-slate-400 mt-0.5">Create campaigns, generate links, and track customer responses</p>
        </div>
        <Button onClick={handleOpenCreate} className="gap-1.5 bg-[#0066CC] hover:bg-[#0052A3] w-full sm:w-auto" data-testid="create-feedback-campaign-btn">
          <Plus className="w-4 h-4" /> New Campaign
        </Button>
      </div>

      {/* Create Campaign Form */}
      {showCreate && (
        <div className="bg-white rounded-xl border-2 border-blue-200 p-4 md:p-5 shadow-sm space-y-4" data-testid="create-feedback-form">
          <div className="flex items-center justify-between">
            <h3 className="text-sm md:text-base font-bold text-slate-900">Create Customer Feedback Campaign</h3>
            <button onClick={() => setShowCreate(false)} className="p-1 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-400" /></button>
          </div>
          <div>
            <Label className="text-sm font-medium">Campaign Title *</Label>
            <Input value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder='e.g., "Post Demo - ABC Corp"' className="mt-1" data-testid="feedback-campaign-title" />
          </div>
          {/* User Selection */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-medium">Select External Users ({selectedEmails.length} selected)</Label>
              <Button variant="outline" size="sm" onClick={selectAll} className="text-xs h-7" data-testid="feedback-select-all">
                {selectedEmails.length === allUsers.length && allUsers.length > 0 ? "Deselect All" : "Select All"}
              </Button>
            </div>
            {selectedEmails.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-3 p-2.5 bg-blue-50/60 rounded-xl border border-blue-100">
                {selectedEmails.map(email => {
                  const u = allUsers.find(usr => usr.email === email);
                  return (
                    <span key={email} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 bg-white border border-blue-200 rounded-lg text-xs font-medium text-slate-700 shadow-sm">
                      {u?.name || email.split("@")[0]}
                      <button onClick={() => toggleEmail(email)} className="p-0.5 rounded hover:bg-red-100 text-slate-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                    </span>
                  );
                })}
              </div>
            )}
            <div className="relative">
              <Input value={userSearch} onChange={e => setUserSearch(e.target.value)} onFocus={() => setSearchFocused(true)} onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
                placeholder="Search by name or email..." className="pr-8 text-sm" data-testid="feedback-user-search" />
              {userSearch && <button onClick={() => setUserSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400"><X className="w-4 h-4" /></button>}
              {searchFocused && (
                <div className="absolute z-10 mt-1 w-full bg-white border rounded-xl shadow-lg max-h-48 overflow-y-auto">
                  {filteredUsers.length === 0 ? (
                    <p className="p-3 text-sm text-slate-400 text-center">{userSearch ? "No matching users" : allUsers.length === 0 ? "No external users found" : "All users selected"}</p>
                  ) : filteredUsers.map(u => (
                    <button key={u.email} onMouseDown={e => { e.preventDefault(); toggleEmail(u.email); setUserSearch(""); }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-blue-50 text-left border-b border-slate-50 last:border-0">
                      <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600 shrink-0">{(u.name || u.email)[0].toUpperCase()}</div>
                      <div className="min-w-0"><p className="text-sm font-medium truncate">{u.name || u.email.split("@")[0]}</p><p className="text-xs text-slate-400 truncate">{u.email}</p></div>
                      <Plus className="w-4 h-4 text-blue-500 ml-auto shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 pt-3 border-t border-slate-100">
            <Button onClick={handleCreate} disabled={creating} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto" data-testid="feedback-create-submit">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              {creating ? "Creating..." : "Create & Send Emails"}
            </Button>
            <p className="text-xs text-slate-400">Emails with feedback link will be sent to selected users</p>
          </div>
        </div>
      )}

      {/* Campaign List */}
      {loading ? <LoadingState /> : campaigns.length === 0 ? <EmptyState icon={MessageSquare} text="No customer feedback campaigns yet. Create your first campaign above." /> : (
        <div className="space-y-3">
          {campaigns.map(c => {
            const isExpanded = expandedCampaign === c.id;
            const respData = campaignResponses[c.id];
            return (
              <div key={c.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`feedback-campaign-${c.id}`}>
                {/* Campaign Header */}
                <div className="p-3 md:p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-9 h-9 rounded-lg bg-teal-100 flex items-center justify-center shrink-0">
                        <MessageSquare className="w-4 h-4 text-teal-600" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-sm font-bold text-slate-900 truncate">{c.title}</h3>
                        <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-500 flex-wrap">
                          <span>{new Date(c.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
                          <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-500" /> {c.response_count}/{c.total_users} responses</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button variant="outline" size="sm" onClick={() => copyLink(c.token, c)} className="gap-1 h-8 text-xs" data-testid={`feedback-copy-${c.id}`}>
                        <Copy className="w-3 h-3" /> Link
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleExport(c.id)} disabled={exporting || c.response_count === 0} className="h-8 text-xs" data-testid={`feedback-export-${c.id}`}>
                        <Download className="w-3 h-3" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleExpand(c.id)} className="h-8" data-testid={`feedback-expand-${c.id}`}>
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDelete(c.id)} className="text-red-600 hover:bg-red-50 h-8" data-testid={`feedback-delete-${c.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Expanded Responses */}
                {isExpanded && (
                  <div className="border-t border-slate-200 p-3 md:p-4 bg-slate-50">
                    {/* Invited users */}
                    <div className="mb-4">
                      <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Invited Users</p>
                      <div className="flex flex-wrap gap-1.5">
                        {(respData?.campaign?.users || c.users || []).map(u => (
                          <span key={u.email} className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${u.submitted ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
                            {u.submitted ? <CheckCircle className="w-3 h-3" /> : <span className="w-3 h-3 rounded-full border border-slate-400 inline-block" />}
                            {u.name || u.email}
                            {!u.submitted && (
                              <button onClick={() => handleCopyUrl(`${window.location.origin}/feedback/${c.token}?email=${encodeURIComponent(u.email)}`)}
                                className="ml-0.5 p-0.5 rounded hover:bg-slate-300" title={`Copy link for ${u.email}`}>
                                <Copy className="w-2.5 h-2.5" />
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Responses */}
                    {!respData ? <Loader2 className="w-5 h-5 animate-spin text-[#0066CC] mx-auto" /> :
                      respData.responses?.length === 0 ? <p className="text-sm text-slate-400 text-center py-4">No responses yet</p> : (
                      <>
                        {/* Desktop Table */}
                        <div className="hidden md:block overflow-x-auto rounded-lg border border-slate-200 bg-white">
                          <table className="w-full text-sm" data-testid="feedback-responses-table">
                            <thead><tr className="bg-slate-50 border-b">
                              <th className="text-left p-3 font-semibold text-slate-700">Prospect</th>
                              <th className="text-left p-3 font-semibold text-slate-700">Company</th>
                              <th className="text-center p-3 font-semibold text-slate-700">Intent</th>
                              <th className="text-center p-3 font-semibold text-slate-700">Demo Rating</th>
                              <th className="text-left p-3 font-semibold text-slate-700">Submitted</th>
                              <th className="text-right p-3 font-semibold text-slate-700">Actions</th>
                            </tr></thead>
                            <tbody>
                              {respData.responses.map(r => (
                                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                                  <td className="p-3"><p className="font-medium">{r.prospect_name || r.user_name || "-"}</p><p className="text-xs text-slate-400">{r.user_email}</p></td>
                                  <td className="p-3 text-slate-600">{r.company_name || "-"}</td>
                                  <td className="p-3 text-center"><IntentBadge intent={r.lead_intent} /></td>
                                  <td className="p-3 text-center"><RatingBadge value={r.responses?.q1} /></td>
                                  <td className="p-3 text-xs text-slate-500">{r.submitted_at ? new Date(r.submitted_at).toLocaleDateString("en-IN") : "-"}</td>
                                  <td className="p-3 text-right">
                                    <div className="flex gap-1 justify-end">
                                      <button onClick={() => setViewFeedback(r)} className="p-1.5 hover:bg-blue-50 rounded-lg" data-testid={`feedback-view-${r.id}`}><Eye className="w-3.5 h-3.5 text-slate-500" /></button>
                                      <button onClick={() => deleteResponse(r.id, c.id)} className="p-1.5 hover:bg-red-50 rounded-lg"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {/* Mobile Cards */}
                        <div className="md:hidden space-y-2">
                          {respData.responses.map(r => (
                            <div key={r.id} className="bg-white rounded-xl border border-slate-200 p-3">
                              <div className="flex items-center justify-between mb-1.5">
                                <p className="font-medium text-sm">{r.prospect_name || r.user_name || "-"}</p>
                                <IntentBadge intent={r.lead_intent} />
                              </div>
                              <p className="text-xs text-slate-500">{r.user_email}</p>
                              <div className="flex items-center justify-between mt-2">
                                <div className="flex items-center gap-2"><RatingBadge value={r.responses?.q1} /><span className="text-[10px] text-slate-400">{r.company_name || ""}</span></div>
                                <div className="flex gap-1">
                                  <button onClick={() => setViewFeedback(r)} className="p-1.5 hover:bg-blue-50 rounded-lg"><Eye className="w-3.5 h-3.5 text-slate-500" /></button>
                                  <button onClick={() => deleteResponse(r.id, c.id)} className="p-1.5 hover:bg-red-50 rounded-lg"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Link Modal */}
      {linkModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setLinkModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full overflow-hidden" onClick={e => e.stopPropagation()} data-testid="feedback-link-modal">
            <div className="flex items-center justify-between p-4 border-b">
              <div><h3 className="text-base font-bold text-slate-900">{linkModal.title}</h3><p className="text-xs text-slate-500">Feedback link & per-user URLs</p></div>
              <button onClick={() => setLinkModal(null)} className="p-1 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Campaign Link</p>
                <div className="flex items-center gap-2">
                  <Input readOnly value={linkModal.url} className="text-sm bg-slate-50 font-mono flex-1" onClick={e => e.target.select()} data-testid="feedback-modal-url" />
                  <Button size="sm" onClick={() => handleCopyUrl(linkModal.url)} className="gap-1 shrink-0 bg-[#0066CC]"><Copy className="w-3.5 h-3.5" /> Copy</Button>
                </div>
              </div>
              {linkModal.users?.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Per-User Links</p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {linkModal.users.map(u => {
                      const userUrl = `${linkModal.url}?email=${encodeURIComponent(u.email)}`;
                      return (
                        <div key={u.email} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg border border-slate-100">
                          <div className="min-w-0 flex-1"><p className="text-sm font-medium text-slate-800 truncate">{u.name}</p><p className="text-[10px] text-slate-400 font-mono truncate">{userUrl}</p></div>
                          <Button variant="outline" size="sm" onClick={() => handleCopyUrl(userUrl)} className="gap-1 shrink-0 h-7 text-xs"><Copy className="w-3 h-3" /></Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Detail View Modal */}
      {viewFeedback && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setViewFeedback(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-testid="feedback-detail-modal">
            <div className="flex items-center justify-between p-4 border-b">
              <div><h3 className="text-base font-bold text-slate-900">{viewFeedback.prospect_name || viewFeedback.user_name || "Unknown"}</h3><p className="text-xs text-slate-500">{viewFeedback.user_email} {viewFeedback.company_name && `| ${viewFeedback.company_name}`}</p></div>
              <button onClick={() => setViewFeedback(null)} className="p-1 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh] space-y-2.5">
              {viewFeedback.lead_intent && (
                <div className="flex items-center gap-2 p-2.5 rounded-lg bg-slate-50"><span className="text-xs font-semibold text-slate-500 uppercase">Intent:</span><IntentBadge intent={viewFeedback.lead_intent} /></div>
              )}
              {Object.entries(CUSTOMER_Q_LABELS).map(([key, label]) => {
                const val = viewFeedback.responses?.[key];
                if (!val || (Array.isArray(val) && val.length === 0)) return null;
                return (
                  <div key={key} className="flex flex-col gap-1 p-2.5 rounded-lg bg-slate-50">
                    <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">{label}</span>
                    {CUSTOMER_RATING_KEYS.includes(key) ? <RatingBadge value={val} /> :
                      Array.isArray(val) ? <ul className="text-sm text-slate-800 space-y-0.5">{val.map((item, i) => <li key={i}>- {item}</li>)}</ul> :
                      <p className="text-sm text-slate-800 leading-relaxed">{val}</p>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============ PRESENTATION GENERATOR TAB ============ */
const QUESTIONS = [
  { id: 1, label: "Understand the business", color: "#0052D4", title: "Business Profile & Day-to-Day Operations", question: "Tell me — how does a normal working day look in your business? From the time an order comes in or a customer calls, what happens step by step?", why: "Opens them up naturally. Most SME owners love talking about how their business runs. You will spot the manual steps, the WhatsApp coordination, the Excel chaos — without asking directly.", listenFor: "anything manual, anything done on phone/WhatsApp, any step where they say 'it depends on the person'. That is your ERP or workflow hook.", tags: ["ERP", "Focus Softnet", "Odoo", "Custom App"], hint: "E.g., 'Orders come via WhatsApp, we enter them in Tally, production planning is on Excel, dispatch tracking is manual.'" },
  { id: 2, label: "Find the pain", color: "#D4380D", title: "The Biggest Headache Right Now", question: "Honestly, what is the one thing in your daily operations that is giving you the most headache right now?", why: "Gets straight to urgency. SME owners are direct — they will tell you what is hurting if you ask simply. The answer points you to exactly which ABSOLIN product to demo.", listenFor: "vendor delays → VendPin. People tracking → HRMS. No visibility on data → InsightIQ. Stock/order chaos → ERP. Asset loss → Asset Mgmt.", tags: ["VendPin", "HCM/HRMS", "Asset Mgmt", "InsightIQ"], hint: "E.g., 'Stock mismatch is killing us — we lose orders because we don't know what's in the warehouse.'" },
  { id: 3, label: "Understand the business", color: "#0052D4", title: "Team Size & Current Tools", question: "How many people are handling this in your team? And are they using any software today — or is it mostly Excel, WhatsApp, and notebooks?", why: "This gives you the headcount and user count for your estimate module. It also tells you if they are greenfield (easier sell) or replacing something (needs migration story).", listenFor: "team size 5–50 is your sweet spot. Excel/WhatsApp heavy = strong ERP case. Already using software = ask what they hate about it.", tags: ["Licence Sizing", "Estimate Module", "Odoo", "Focus X"], hint: "E.g., '15 people across sales, stores, and accounts. Everything is on Tally and Excel.'" },
  { id: 4, label: "Find the pain", color: "#D4380D", title: "Past Attempts & Lessons", question: "Have you tried fixing this before — maybe with some software or a consultant? What happened?", why: "Surfaces past bad experiences with vendors. This is your chance to position ABSOLIN's delivery guarantee and hands-on implementation approach — not by pitching, but by asking.", listenFor: "'vendor disappeared after payment', 'no training', 'too complicated for my team', 'cost went up'. These are direct openings for your USP.", tags: ["Delivery Guarantee", "Implementation", "Trust Building"], hint: "E.g., 'We tried a local software 2 years ago — vendor vanished after taking the money.'" },
  { id: 5, label: "Decision & budget", color: "#389E0D", title: "Decision Makers & Process", question: "When you decide on something like this — is it just you, or are there others in the family or management you check with first?", why: "Identifies the real decision-maker without making it awkward. In MSMEs it is often the owner, spouse, or a senior partner. Bringing the right people to the POC removes the 'let me check' delay.", listenFor: "'my partner', 'my MD', 'my accountant', 'my son handles that'. Invite all of them to the demo. One person missing = deal stuck.", tags: ["KDM Mapping", "Decision Unit", "Proposal Strategy"], hint: "E.g., 'I decide, but my partner handles finance — he needs to be in the demo too.'" },
  { id: 6, label: "Tech comfort", color: "#722ED1", title: "Tech Comfort Level", question: "Are you and your team comfortable using mobile apps and computers for work — or is it more phone calls and paper for now?", why: "Assesses tech readiness so you pitch the right product at the right complexity. A low-tech team needs a simple UI demo, not an AI dashboard walkthrough first.", listenFor: "'my team is young and picks things fast' → go deeper on AI/dashboard. 'My staff is not very educated' → lead with simple ERP or mobile-first app. Sets your POC tone.", tags: ["POC Complexity", "AI Readiness", "InsightIQ", "Training Plan"], hint: "E.g., 'Most of my guys use smartphones. The accounts team is okay with computers.'" },
  { id: 7, label: "Set up the demo", color: "#08979C", title: "Timeline, Budget & Demo Setup", question: "If we come back and show you a live demo — using your kind of business as an example — and it solves exactly what you described, how soon would you want to move forward? And roughly what kind of budget are you thinking?", why: "This is your commitment check. If they give a timeline and a rough number, they are a hot lead. If they go vague, the pain is not urgent enough — note for nurture and move on.", listenFor: "'this quarter', 'after Diwali', 'around 5–10 lakhs'. Vague answers like 'let us see' = re-qualify. No budget talk at all = possibly not the decision-maker.", tags: ["Budget Signal", "Hot Lead Flag", "Demo Booking"], hint: "E.g., 'If the demo is good, we can start next month. Budget around 5-8 lakhs.'" },
];

const LEGEND = [
  { color: "#0052D4", label: "Understand the business" },
  { color: "#D4380D", label: "Find the pain" },
  { color: "#389E0D", label: "Decision & budget" },
  { color: "#722ED1", label: "Tech comfort" },
  { color: "#08979C", label: "Set up the demo" },
];

function PresentationTab() {
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadSearch, setLeadSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [proposedSolution, setProposedSolution] = useState("");
  const [overview, setOverview] = useState("");
  const [responses, setResponses] = useState({});
  const [step, setStep] = useState(1);
  const [generating, setGenerating] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [history, setHistory] = useState([]);
  const [expandedQ, setExpandedQ] = useState(null);

  useEffect(() => { api.get("/presales/leads?status=all").then(r => setLeads(r.data || [])).catch(() => {}); api.get("/presentations/history").then(r => setHistory(r.data || [])).catch(() => {}); }, []);

  const filtered = leads.filter(l => { const q = leadSearch.toLowerCase(); return !q || (l.name||"").toLowerCase().includes(q) || (l.email||"").toLowerCase().includes(q) || (l.company||"").toLowerCase().includes(q); });
  const answered = Object.values(responses).filter(v => v.trim()).length;

  const generate = async () => {
    if (!selectedLead) return;
    setGenerating(true);
    try {
      const r = await api.post("/presentations/generate", { lead_id: selectedLead.id, proposed_solution: proposedSolution, alignment_overview: overview, responses });
      setPrompt(r.data.prompt);
      setStep(4);
      toast.success("Prompt generated with Claude AI!");
    } catch (err) { toast.error(err.response?.data?.detail || "Generation failed"); }
    setGenerating(false);
  };

  const copyPrompt = async () => {
    try { await navigator.clipboard.writeText(prompt); toast.success("Copied!"); }
    catch { const t = document.createElement("textarea"); t.value = prompt; t.style.cssText = "position:fixed;left:-9999px"; document.body.appendChild(t); t.select(); document.execCommand("copy"); document.body.removeChild(t); toast.success("Copied!"); }
  };

  const reset = () => { setStep(1); setSelectedLead(null); setProposedSolution(""); setOverview(""); setResponses({}); setPrompt(""); setExpandedQ(null); };

  return (
    <div className="p-4 md:p-5">
      {/* Step 1: Select Lead */}
      {step === 1 && (
        <div className="space-y-4" data-testid="pres-step-1">
          <h2 className="text-base md:text-lg font-semibold text-slate-900">Select a Lead</h2>
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={leadSearch} onChange={e => setLeadSearch(e.target.value)} onFocus={() => setShowDropdown(true)} onBlur={() => setTimeout(() => setShowDropdown(false), 200)} placeholder="Search leads..." className="pl-9" data-testid="pres-lead-search" />
            {showDropdown && (
              <div className="absolute z-10 mt-1 w-full bg-white border rounded-xl shadow-lg max-h-52 overflow-y-auto">
                {filtered.length === 0 ? <p className="p-3 text-sm text-slate-400 text-center">No leads found</p> : filtered.slice(0, 20).map(l => (
                  <button key={l.id} onMouseDown={e => { e.preventDefault(); setSelectedLead(l); setLeadSearch(""); setShowDropdown(false); }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-blue-50 text-left border-b border-slate-50 last:border-0">
                    <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center text-sm font-bold text-blue-600 shrink-0">{(l.name||"?")[0].toUpperCase()}</div>
                    <div className="min-w-0"><p className="text-sm font-medium truncate">{l.name}</p><p className="text-xs text-slate-400 truncate">{l.company || l.email}</p></div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {selectedLead && (
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-100 flex flex-col sm:flex-row items-start sm:items-center gap-3 max-w-md" data-testid="pres-selected-lead">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-blue-200 flex items-center justify-center font-bold text-blue-700 shrink-0">{(selectedLead.name||"?")[0].toUpperCase()}</div>
                <div className="min-w-0"><p className="font-semibold text-blue-900 truncate">{selectedLead.name}</p><p className="text-xs text-blue-600 truncate">{selectedLead.company || selectedLead.email}</p></div>
              </div>
              <Button size="sm" onClick={() => setStep(2)} className="bg-[#0066CC] gap-1 w-full sm:w-auto" data-testid="pres-next-1">Next <ArrowRight className="w-3.5 h-3.5" /></Button>
            </div>
          )}
          {history.length > 0 && (
            <div className="mt-6"><h3 className="text-sm font-semibold text-slate-700 mb-2">Recent</h3>
              {history.slice(0,3).map(h => (
                <div key={h.id} className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 bg-slate-50 rounded-lg border mb-2 gap-2">
                  <div><p className="text-sm font-medium">{h.lead_name} — {h.lead_company || "N/A"}</p><p className="text-xs text-slate-400">{new Date(h.created_at).toLocaleDateString()}</p></div>
                  <Button variant="outline" size="sm" onClick={() => { setPrompt(h.prompt); setStep(4); }}>View</Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Step 2: Context */}
      {step === 2 && (
        <div className="space-y-4 max-w-2xl" data-testid="pres-step-2">
          <h2 className="text-base md:text-lg font-semibold">Alignment Context — {selectedLead?.name}</h2>
          <div className="space-y-3">
            <div><Label className="text-sm">Proposed Solution from ABSOLIN</Label><Textarea value={proposedSolution} onChange={e => setProposedSolution(e.target.value)} placeholder="e.g., SAP B1 with CRM + Inventory integration..." rows={2} className="mt-1" data-testid="pres-solution" /></div>
            <div><Label className="text-sm">Overview of the Alignment Call</Label><Textarea value={overview} onChange={e => setOverview(e.target.value)} placeholder="e.g., Meeting with owner to discuss digitization..." rows={2} className="mt-1" data-testid="pres-overview" /></div>
          </div>
          <div className="flex justify-between pt-3 border-t gap-2">
            <Button variant="outline" onClick={() => setStep(1)} className="gap-1"><ArrowLeft className="w-3.5 h-3.5" /> Back</Button>
            <Button onClick={() => setStep(3)} className="bg-[#0066CC] gap-1" data-testid="pres-next-2">7 Alignment Questions <ArrowRight className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
      )}

      {/* Step 3: 7 Alignment Questions — Card-based UI */}
      {step === 3 && (
        <div className="space-y-4" data-testid="pres-step-3">
          {/* Header */}
          <div className="border-b border-slate-200 pb-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider font-medium">First Discovery Call</p>
                <h2 className="text-base md:text-lg font-semibold text-slate-900 mt-0.5">7 Pre-Sales Alignment Questions</h2>
              </div>
              <span className="text-sm font-medium px-3 py-1 bg-blue-50 text-[#0052D4] rounded-full self-start">{answered}/7 answered</span>
            </div>
            {/* Legend */}
            <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3">
              {LEGEND.map(leg => (
                <div key={leg.label} className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: leg.color }} />
                  <span className="text-[11px] text-slate-500">{leg.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Question Cards */}
          <div className="space-y-3 max-w-3xl">
            {QUESTIONS.map(q => {
              const isExpanded = expandedQ === q.id;
              const hasResponse = responses[String(q.id)]?.trim();
              return (
                <div key={q.id} className="rounded-xl border transition-all" style={{ borderColor: isExpanded ? q.color : hasResponse ? '#86efac' : '#e2e8f0', borderWidth: isExpanded ? '1.5px' : '1px' }}
                  data-testid={`pres-card-${q.id}`}>
                  {/* Card Header — clickable to expand insights */}
                  <div className="flex gap-3 md:gap-4 p-3.5 md:p-4 cursor-pointer" onClick={() => setExpandedQ(isExpanded ? null : q.id)}>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium shrink-0" style={{ background: q.color }}>
                      {hasResponse ? <CheckCircle className="w-4 h-4" /> : q.id}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-medium uppercase tracking-wider mb-1" style={{ color: q.color }}>{q.label}</p>
                      <p className="text-sm md:text-[15px] font-medium text-slate-900 leading-relaxed">{q.question}</p>
                      <p className="text-[11px] text-slate-400 mt-1.5">{isExpanded ? "tap to hide insights" : "tap to see what to listen for"}</p>

                      {/* Expanded Insights */}
                      {isExpanded && (
                        <div className="mt-3 space-y-2.5" onClick={e => e.stopPropagation()}>
                          <p className="text-xs text-slate-500 leading-relaxed"><span className="font-semibold text-slate-700">Why ask this:</span> {q.why}</p>
                          <div className="rounded-lg p-2.5 text-xs leading-relaxed" style={{ background: `${q.color}08`, borderLeft: `3px solid ${q.color}`, color: '#374151' }}>
                            <span className="font-semibold">Listen for:</span> {q.listenFor}
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {q.tags.map(tag => (
                              <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ background: `${q.color}12`, color: q.color }}>{tag}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Response Input — always visible, properly aligned below the card content */}
                  <div className="px-3.5 md:px-4 pb-3.5 md:pb-4 pt-0">
                    <div className="pl-11 md:pl-12">
                      <Textarea
                        value={responses[String(q.id)] || ""}
                        onChange={e => setResponses({...responses, [String(q.id)]: e.target.value})}
                        placeholder={q.hint}
                        rows={2}
                        className="text-sm w-full resize-y placeholder:text-slate-300"
                        onClick={e => e.stopPropagation()}
                        data-testid={`pres-q-${q.id}`}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Navigation */}
          <div className="flex flex-col sm:flex-row justify-between pt-3 border-t gap-3">
            <Button variant="outline" onClick={() => setStep(2)} className="gap-1"><ArrowLeft className="w-3.5 h-3.5" /> Back</Button>
            <Button onClick={generate} disabled={generating} className="bg-gradient-to-r from-purple-600 to-indigo-600 gap-2 px-6" data-testid="pres-generate">
              {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</> : <><Sparkles className="w-4 h-4" /> Generate Prompt (Claude AI)</>}
            </Button>
          </div>
        </div>
      )}

      {/* Step 4: Result */}
      {step === 4 && (
        <div className="space-y-4" data-testid="pres-step-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
            <h2 className="text-base md:text-lg font-semibold flex items-center gap-2"><CheckCircle className="w-5 h-5 text-emerald-500" /> Presentation Prompt Ready</h2>
            <Button onClick={copyPrompt} className="bg-[#0066CC] gap-2 w-full sm:w-auto" data-testid="pres-copy"><Copy className="w-4 h-4" /> Copy Prompt</Button>
          </div>
          <p className="text-sm text-slate-500">Paste this into <a href="https://gamma.app" target="_blank" rel="noopener noreferrer" className="text-[#0066CC] underline">Gamma.app</a> or any AI presentation tool.</p>
          <div className="bg-slate-900 text-slate-100 rounded-xl p-4 md:p-5 font-mono text-xs md:text-sm leading-relaxed max-h-[50vh] overflow-y-auto whitespace-pre-wrap" data-testid="pres-prompt">{prompt}</div>
          <Button variant="outline" onClick={reset} className="gap-1" data-testid="pres-new"><Presentation className="w-4 h-4" /> New Presentation</Button>
        </div>
      )}
    </div>
  );
}

/* ============ BOOKINGS TAB ============ */
function BookingsTab() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => { load(); }, [filter]);
  const load = async () => { setLoading(true); try { const r = await api.get(filter !== "all" ? `/presales/bookings?source=${filter}` : "/presales/bookings"); setBookings(r.data || []); } catch {} setLoading(false); };

  return (
    <div className="p-4 md:p-5">
      <div className="flex flex-wrap gap-2 mb-4">
        {[{ k: "all", l: "All" }, { k: "calendly_booking", l: "Demos" }, { k: "consultation_booking", l: "Consultations" }].map(f => (
          <button key={f.k} onClick={() => setFilter(f.k)} className={`px-3 py-1.5 rounded-lg text-xs md:text-sm font-medium ${filter === f.k ? "bg-[#0066CC] text-white" : "bg-slate-100 text-slate-600"}`}>{f.l}</button>
        ))}
        <span className="text-xs md:text-sm text-slate-400 self-center">{bookings.length} records</span>
      </div>
      {loading ? <LoadingState /> : bookings.length === 0 ? <EmptyState icon={Calendar} text="No booking submissions" /> : (
        <>
          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto"><table className="w-full text-sm"><thead><tr className="bg-slate-50 border-b"><th className="text-left px-3 py-2 font-semibold text-slate-600">Name</th><th className="text-left px-3 py-2 font-semibold text-slate-600">Email</th><th className="text-left px-3 py-2 font-semibold text-slate-600">Company</th><th className="text-left px-3 py-2 font-semibold text-slate-600">Type</th><th className="text-left px-3 py-2 font-semibold text-slate-600">Date</th></tr></thead>
            <tbody>{bookings.map(b => (
              <tr key={b.id} className="border-b border-slate-100 hover:bg-slate-50"><td className="px-3 py-2 font-medium">{b.name}</td><td className="px-3 py-2 text-xs text-slate-500">{b.email}</td><td className="px-3 py-2">{b.company || "—"}</td>
                <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded text-xs font-medium ${(b.source === "calendly_booking" || b.source === "odoo_booking") ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"}`}>{(b.source === "calendly_booking" || b.source === "odoo_booking") ? "Demo" : "Consultation"}</span></td>
                <td className="px-3 py-2 text-xs text-slate-400">{b.created_at ? new Date(b.created_at).toLocaleDateString() : "—"}</td></tr>
            ))}</tbody></table></div>
          {/* Mobile Cards */}
          <div className="md:hidden space-y-2">
            {bookings.map(b => (
              <div key={b.id} className="bg-white rounded-xl border border-slate-200 p-3">
                <div className="flex items-center justify-between mb-1">
                  <p className="font-medium text-sm">{b.name}</p>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${(b.source === "calendly_booking" || b.source === "odoo_booking") ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"}`}>{(b.source === "calendly_booking" || b.source === "odoo_booking") ? "Demo" : "Consult"}</span>
                </div>
                <p className="text-xs text-slate-500">{b.email}</p>
                <div className="flex items-center justify-between mt-1.5">
                  <p className="text-xs text-slate-400">{b.company || "—"}</p>
                  <p className="text-[10px] text-slate-400">{b.created_at ? new Date(b.created_at).toLocaleDateString() : "—"}</p>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ============ ASSESSMENTS TAB ============ */
function AssessmentsTab() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/presales/assessments").then(r => setData(r.data || [])).catch(() => {}).finally(() => setLoading(false)); }, []);

  return (
    <div className="p-4 md:p-5">
      <h2 className="text-base md:text-lg font-semibold mb-4">Digital Maturity Assessment Results</h2>
      {loading ? <LoadingState /> : data.length === 0 ? <EmptyState icon={BarChart3} text="No assessment results yet" /> : (
        <>
          <div className="hidden md:block overflow-x-auto"><table className="w-full text-sm"><thead><tr className="bg-slate-50 border-b"><th className="text-left px-3 py-2 font-semibold text-slate-600">User</th><th className="text-left px-3 py-2 font-semibold text-slate-600">Overall Score</th><th className="text-left px-3 py-2 font-semibold text-slate-600">Maturity Level</th><th className="text-left px-3 py-2 font-semibold text-slate-600">Date</th></tr></thead>
            <tbody>{data.map((d, i) => (
              <tr key={d.id || i} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-2 font-medium">{d.user_email || d.user_name || "—"}</td>
                <td className="px-3 py-2"><span className="text-lg font-bold text-[#0066CC]">{d.overall_score || d.total_score || "—"}</span><span className="text-xs text-slate-400">/100</span></td>
                <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded text-xs font-medium ${(d.maturity_level || "").includes("Leader") ? "bg-emerald-100 text-emerald-700" : (d.maturity_level || "").includes("Developing") ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{d.maturity_level || "—"}</span></td>
                <td className="px-3 py-2 text-xs text-slate-400">{d.created_at ? new Date(d.created_at).toLocaleDateString() : "—"}</td>
              </tr>
            ))}</tbody></table></div>
          <div className="md:hidden space-y-2">
            {data.map((d, i) => (
              <div key={d.id || i} className="bg-white rounded-xl border border-slate-200 p-3">
                <p className="font-medium text-sm">{d.user_email || d.user_name || "—"}</p>
                <div className="flex items-center justify-between mt-1.5">
                  <div className="flex items-baseline gap-1"><span className="text-lg font-bold text-[#0066CC]">{d.overall_score || d.total_score || "—"}</span><span className="text-xs text-slate-400">/100</span></div>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${(d.maturity_level || "").includes("Leader") ? "bg-emerald-100 text-emerald-700" : (d.maturity_level || "").includes("Developing") ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{d.maturity_level || "—"}</span>
                </div>
                <p className="text-[10px] text-slate-400 mt-1">{d.created_at ? new Date(d.created_at).toLocaleDateString() : "—"}</p>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ============ CODN TAB ============ */
function CODNTab() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/presales/codn").then(r => setData(r.data || [])).catch(() => {}).finally(() => setLoading(false)); }, []);

  return (
    <div className="p-4 md:p-5">
      <h2 className="text-base md:text-lg font-semibold mb-4">Cost of Doing Nothing Results</h2>
      {loading ? <LoadingState /> : data.length === 0 ? <EmptyState icon={Calculator} text="No CODN results yet" /> : (
        <>
          <div className="hidden md:block overflow-x-auto"><table className="w-full text-sm"><thead><tr className="bg-slate-50 border-b"><th className="text-left px-3 py-2 font-semibold text-slate-600">User</th><th className="text-left px-3 py-2 font-semibold text-slate-600">Annual Cost</th><th className="text-left px-3 py-2 font-semibold text-slate-600">5-Year Impact</th><th className="text-left px-3 py-2 font-semibold text-slate-600">Date</th></tr></thead>
            <tbody>{data.map((d, i) => (
              <tr key={d.id || i} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-2 font-medium">{d.user_email || "—"}</td>
                <td className="px-3 py-2 text-red-600 font-bold">₹{((d.results?.total_annual_cost || 0) / 100000).toFixed(1)}L</td>
                <td className="px-3 py-2 text-red-500 font-medium">₹{((d.results?.five_year_cost || 0) / 100000).toFixed(1)}L</td>
                <td className="px-3 py-2 text-xs text-slate-400">{d.created_at ? new Date(d.created_at).toLocaleDateString() : "—"}</td>
              </tr>
            ))}</tbody></table></div>
          <div className="md:hidden space-y-2">
            {data.map((d, i) => (
              <div key={d.id || i} className="bg-white rounded-xl border border-slate-200 p-3">
                <p className="font-medium text-sm">{d.user_email || "—"}</p>
                <div className="flex items-center justify-between mt-1.5">
                  <div><p className="text-xs text-slate-400">Annual</p><p className="text-red-600 font-bold text-sm">₹{((d.results?.total_annual_cost || 0) / 100000).toFixed(1)}L</p></div>
                  <div><p className="text-xs text-slate-400">5-Year</p><p className="text-red-500 font-medium text-sm">₹{((d.results?.five_year_cost || 0) / 100000).toFixed(1)}L</p></div>
                  <p className="text-[10px] text-slate-400">{d.created_at ? new Date(d.created_at).toLocaleDateString() : "—"}</p>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ============ MAPPER TAB ============ */
function MapperTab() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/presales/system-mapper").then(r => setData(r.data || [])).catch(() => {}).finally(() => setLoading(false)); }, []);

  return (
    <div className="p-4 md:p-5">
      <h2 className="text-base md:text-lg font-semibold mb-4">System Landscape Mapper Results</h2>
      {loading ? <LoadingState /> : data.length === 0 ? <EmptyState icon={Network} text="No system mapping results yet" /> : (
        <>
          <div className="hidden md:block overflow-x-auto"><table className="w-full text-sm"><thead><tr className="bg-slate-50 border-b"><th className="text-left px-3 py-2 font-semibold text-slate-600">User</th><th className="text-left px-3 py-2 font-semibold text-slate-600">Departments</th><th className="text-left px-3 py-2 font-semibold text-slate-600">Integration Score</th><th className="text-left px-3 py-2 font-semibold text-slate-600">Date</th></tr></thead>
            <tbody>{data.map((d, i) => (
              <tr key={d.id || i} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-2 font-medium">{d.user_email || "—"}</td>
                <td className="px-3 py-2">{d.metrics?.total_departments || d.departments?.length || "—"}</td>
                <td className="px-3 py-2"><span className="font-bold text-[#0066CC]">{d.metrics?.integration_score || "—"}</span></td>
                <td className="px-3 py-2 text-xs text-slate-400">{d.created_at ? new Date(d.created_at).toLocaleDateString() : "—"}</td>
              </tr>
            ))}</tbody></table></div>
          <div className="md:hidden space-y-2">
            {data.map((d, i) => (
              <div key={d.id || i} className="bg-white rounded-xl border border-slate-200 p-3">
                <p className="font-medium text-sm">{d.user_email || "—"}</p>
                <div className="flex items-center justify-between mt-1.5">
                  <div><p className="text-xs text-slate-400">Depts</p><p className="text-sm font-medium">{d.metrics?.total_departments || d.departments?.length || "—"}</p></div>
                  <div><p className="text-xs text-slate-400">Score</p><p className="text-sm font-bold text-[#0066CC]">{d.metrics?.integration_score || "—"}</p></div>
                  <p className="text-[10px] text-slate-400">{d.created_at ? new Date(d.created_at).toLocaleDateString() : "—"}</p>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ============ AUDIT TAB ============ */
function AuditTab() {
  const [logs, setLogs] = useState([]);
  const [analytics, setAnalytics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("analytics"); // "analytics" | "logs"

  useEffect(() => {
    Promise.all([
      api.get("/presales/audit-log").then(r => setLogs(r.data || [])).catch(() => {}),
      api.get("/presales/lead-analytics").then(r => setAnalytics(r.data || [])).catch(() => {})
    ]).finally(() => setLoading(false));
  }, []);

  const formatDetails = (details) => {
    if (!details) return "—";
    if (typeof details === "string") return details;
    if (typeof details === "object") {
      return Object.entries(details).map(([k, v]) => `${k}: ${v}`).join(", ");
    }
    return String(details);
  };

  const TempBadge = ({ temp }) => {
    const map = {
      hot: { bg: "bg-red-100 text-red-700 border-red-200", label: "Hot", icon: "text-red-500" },
      warm: { bg: "bg-amber-100 text-amber-700 border-amber-200", label: "Warm", icon: "text-amber-500" },
      cold: { bg: "bg-blue-100 text-blue-700 border-blue-200", label: "Cold", icon: "text-blue-500" },
      new: { bg: "bg-slate-100 text-slate-500 border-slate-200", label: "New", icon: "text-slate-400" },
    };
    const s = map[temp] || map.new;
    return <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border ${s.bg}`} data-testid={`temp-badge-${temp}`}>{s.label}</span>;
  };

  return (
    <div className="p-4 md:p-5">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <h2 className="text-base md:text-lg font-semibold" data-testid="audit-title">External User Activity</h2>
        <div className="flex bg-slate-100 rounded-lg p-0.5">
          <button onClick={() => setView("analytics")} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${view === "analytics" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`} data-testid="view-analytics">
            Lead Insights
          </button>
          <button onClick={() => setView("logs")} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${view === "logs" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`} data-testid="view-logs">
            Activity Log
          </button>
        </div>
      </div>

      {loading ? <LoadingState /> : view === "analytics" ? (
        /* ---- ANALYTICS VIEW ---- */
        analytics.length === 0 ? <EmptyState icon={BarChart3} text="No external user activity yet" /> : (
          <>
            {/* Summary bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
              <div className="bg-red-50 border border-red-100 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-red-600">{analytics.filter(a => a.temperature === "hot").length}</p>
                <p className="text-[10px] font-semibold text-red-500 uppercase tracking-wider mt-0.5">Hot Leads</p>
              </div>
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-amber-600">{analytics.filter(a => a.temperature === "warm").length}</p>
                <p className="text-[10px] font-semibold text-amber-500 uppercase tracking-wider mt-0.5">Warm</p>
              </div>
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-blue-600">{analytics.filter(a => a.temperature === "cold").length}</p>
                <p className="text-[10px] font-semibold text-blue-500 uppercase tracking-wider mt-0.5">Cold</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-slate-600">{analytics.filter(a => a.temperature === "new").length}</p>
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mt-0.5">New / No Activity</p>
              </div>
            </div>

            {/* User cards */}
            <div className="space-y-3" data-testid="lead-analytics-list">
              {analytics.map((a) => (
                <div key={a.user_id} className={`bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow ${a.is_active === false ? "opacity-50" : ""}`} data-testid={`analytics-card-${a.email}`}>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#0066CC] to-[#0052A3] flex items-center justify-center text-white font-bold text-sm shrink-0">
                        {(a.name || a.email || "?").charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-semibold text-sm text-slate-900">{a.name || a.email}</p>
                        <p className="text-xs text-slate-400">{a.email} {a.company && `· ${a.company}`}</p>
                      </div>
                    </div>
                    <TempBadge temp={a.temperature} />
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                    <div className="bg-slate-50 rounded-lg p-2.5 text-center">
                      <p className="text-lg font-bold text-slate-800">{a.total_time_display}</p>
                      <p className="text-[10px] text-slate-500 font-medium uppercase">Time Spent</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-2.5 text-center">
                      <p className="text-lg font-bold text-slate-800">{a.total_sessions}</p>
                      <p className="text-[10px] text-slate-500 font-medium uppercase">Sessions</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-2.5 text-center">
                      <p className="text-lg font-bold text-slate-800">{a.pages_visited}</p>
                      <p className="text-[10px] text-slate-500 font-medium uppercase">Pages</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-2.5 text-center">
                      <p className="text-lg font-bold text-slate-800">{a.problems_viewed}</p>
                      <p className="text-[10px] text-slate-500 font-medium uppercase">Problems</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-2.5 text-center">
                      <p className="text-lg font-bold text-slate-800">{a.ai_questions}</p>
                      <p className="text-[10px] text-slate-500 font-medium uppercase">AI Queries</p>
                    </div>
                  </div>

                  {(a.industries_accessed?.length > 0 || a.last_active) && (
                    <div className="flex flex-wrap items-center gap-2 mt-2.5 text-xs">
                      {a.industries_accessed?.length > 0 && (
                        <span className="text-slate-400">Industries: <span className="text-slate-600 font-medium">{a.industries_accessed.slice(0, 3).join(", ")}{a.industries_accessed.length > 3 ? ` +${a.industries_accessed.length - 3}` : ""}</span></span>
                      )}
                      {a.last_active && (
                        <span className="text-slate-400 ml-auto">Last active: <span className="text-slate-600">{new Date(a.last_active).toLocaleDateString()}</span></span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )
      ) : (
        /* ---- ACTIVITY LOG VIEW ---- */
        logs.length === 0 ? <EmptyState icon={FileText} text="No activity logs yet" /> : (
          <>
            <div className="hidden md:block overflow-x-auto"><table className="w-full text-sm"><thead><tr className="bg-slate-50 border-b"><th className="text-left px-3 py-2 font-semibold text-slate-600">User</th><th className="text-left px-3 py-2 font-semibold text-slate-600">Action</th><th className="text-left px-3 py-2 font-semibold text-slate-600">Details</th><th className="text-left px-3 py-2 font-semibold text-slate-600">Time</th></tr></thead>
              <tbody>{logs.map((l, i) => (
                <tr key={l.id || i} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2"><p className="font-medium text-sm">{l.user_name || l.user_email || "—"}</p><p className="text-xs text-slate-400">{l.user_email || ""}</p></td>
                  <td className="px-3 py-2"><span className="px-2 py-0.5 bg-slate-100 rounded text-xs font-medium text-slate-700">{l.action}</span></td>
                  <td className="px-3 py-2 text-xs text-slate-500 max-w-[200px] truncate">{formatDetails(l.details)}</td>
                  <td className="px-3 py-2 text-xs text-slate-400">{l.timestamp ? new Date(l.timestamp).toLocaleString() : "—"}</td>
                </tr>
              ))}</tbody></table></div>
            <div className="md:hidden space-y-2">
              {logs.map((l, i) => (
                <div key={l.id || i} className="bg-white rounded-xl border border-slate-200 p-3">
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-medium text-sm">{l.user_name || l.user_email || "—"}</p>
                    <span className="px-2 py-0.5 bg-slate-100 rounded text-[10px] font-medium text-slate-700">{l.action}</span>
                  </div>
                  <p className="text-xs text-slate-500 truncate">{formatDetails(l.details)}</p>
                  <p className="text-[10px] text-slate-400 mt-1">{l.timestamp ? new Date(l.timestamp).toLocaleString() : "—"}</p>
                </div>
              ))}
            </div>
          </>
        )
      )}
    </div>
  );
}

/* ============ DEMO LINKS TAB ============ */
function DemoLinksTab() {
  const [products, setProducts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [leadSearch, setLeadSearch] = useState("");
  const [showLeadDrop, setShowLeadDrop] = useState(false);
  const [generated, setGenerated] = useState(null);

  useEffect(() => { load(); }, []);
  const load = async () => { setLoading(true); try { const [p, l] = await Promise.all([api.get("/presales/products"), api.get("/presales/leads?status=all")]); setProducts(p.data || []); setLeads(l.data || []); } catch {} setLoading(false); };

  const toggleProduct = (id) => setSelectedProducts(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);

  const generateLinks = async () => {
    if (!selectedLead || selectedProducts.length === 0) { toast.error("Select a lead and at least one product"); return; }
    try {
      const r = await api.post("/presales/demo-links/generate", { lead_id: selectedLead.id, product_ids: selectedProducts });
      setGenerated(r.data);
      toast.success("Demo links generated!");
    } catch { toast.error("Failed"); }
  };

  const copyAll = async () => {
    if (!generated) return;
    const text = (generated.products || []).map(p => {
      let t = `${p.name}\nDemo URL: ${p.demo_url || "N/A"}`;
      if (p.credentials) {
        Object.entries(p.credentials).forEach(([role, c]) => {
          if (c?.username) t += `\n${role.toUpperCase()} — User: ${c.username} | Pass: ${c.password}`;
        });
      }
      return t;
    }).join("\n\n");
    try { await navigator.clipboard.writeText(text); toast.success("All links & credentials copied!"); }
    catch { toast.success("Copied!"); }
  };

  const filteredLeads = leads.filter(l => { const q = leadSearch.toLowerCase(); return !q || (l.name||"").toLowerCase().includes(q) || (l.email||"").toLowerCase().includes(q); });

  return (
    <div className="p-4 md:p-5 space-y-5">
      {/* Step 1: Select Lead */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-[#0066CC] text-white text-xs flex items-center justify-center font-bold">1</span>
          Select Lead
        </h3>
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={leadSearch} onChange={e => setLeadSearch(e.target.value)} onFocus={() => setShowLeadDrop(true)} onBlur={() => setTimeout(() => setShowLeadDrop(false), 200)}
            placeholder="Search leads by name or email..." className="pl-9 text-sm" data-testid="demo-lead-search" />
          {showLeadDrop && (
            <div className="absolute z-10 mt-1 w-full bg-white border rounded-xl shadow-lg max-h-48 overflow-y-auto">
              {filteredLeads.length === 0 ? <p className="p-3 text-sm text-slate-400 text-center">No leads found</p> :
                filteredLeads.slice(0, 10).map(l => (
                <button key={l.id} onMouseDown={e => { e.preventDefault(); setSelectedLead(l); setLeadSearch(""); setShowLeadDrop(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-blue-50 text-left border-b border-slate-50 last:border-0">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center text-xs font-bold text-blue-600 shrink-0">{(l.name||"?")[0].toUpperCase()}</div>
                  <div className="min-w-0"><p className="text-sm font-medium truncate">{l.name}</p><p className="text-xs text-slate-400 truncate">{l.company || l.email}</p></div>
                </button>
              ))}
            </div>
          )}
        </div>
        {selectedLead && (
          <div className="mt-3 flex items-center gap-3 bg-blue-50 rounded-lg p-3 border border-blue-100 max-w-sm">
            <div className="w-9 h-9 rounded-lg bg-blue-200 flex items-center justify-center font-bold text-blue-700 text-sm shrink-0">{(selectedLead.name||"?")[0].toUpperCase()}</div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-blue-900 truncate">{selectedLead.name}</p>
              <p className="text-xs text-blue-600 truncate">{selectedLead.company || selectedLead.email}</p>
            </div>
            <button onClick={() => setSelectedLead(null)} className="text-blue-400 hover:text-blue-600 p-1 shrink-0">
              <span className="text-xs font-medium">Change</span>
            </button>
          </div>
        )}
      </div>

      {/* Step 2: Select Products */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-[#0066CC] text-white text-xs flex items-center justify-center font-bold">2</span>
            Select Products
            {selectedProducts.length > 0 && <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">{selectedProducts.length} selected</span>}
          </h3>
          <p className="text-xs text-slate-400">Products managed in Admin Panel</p>
        </div>

        {loading ? <LoadingState /> : products.length === 0 ? (
          <div className="text-center py-8 bg-slate-50 rounded-xl border border-dashed border-slate-200">
            <Link2 className="w-8 h-8 text-slate-200 mx-auto mb-2" />
            <p className="text-sm text-slate-400">No products configured yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {products.map(p => {
              const isSelected = selectedProducts.includes(p.id);
              return (
                <div key={p.id} className={`border rounded-xl p-3 transition-all cursor-pointer ${isSelected ? "border-[#0066CC] bg-blue-50/50 shadow-sm ring-1 ring-[#0066CC]/20" : "border-slate-200 hover:border-slate-300 hover:shadow-sm"}`}
                  onClick={() => toggleProduct(p.id)} data-testid={`product-card-${p.id}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${isSelected ? "bg-[#0066CC] border-[#0066CC]" : "border-slate-300"}`}>
                      {isSelected && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-sm text-slate-900">{p.name}</p>
                        {p.category && <span className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded text-slate-500">{p.category}</span>}
                      </div>
                      {p.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{p.description}</p>}
                      {p.demo_url && <p className="text-xs text-blue-500 truncate flex items-center gap-1 mt-1.5"><Globe className="w-3 h-3 shrink-0" /><span className="truncate">{p.demo_url}</span></p>}
                      {p.credentials && Object.keys(p.credentials).some(k => p.credentials[k]?.username) && (
                        <div className="flex gap-1 mt-2">
                          {Object.entries(p.credentials).map(([role, creds]) => creds?.username && (
                            <span key={role} className="text-[10px] bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded font-medium text-emerald-600 capitalize">{role}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Step 3: Generate */}
      {products.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3">
            <span className="w-6 h-6 rounded-full bg-[#0066CC] text-white text-xs flex items-center justify-center font-bold">3</span>
            Generate Demo Package
          </h3>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="text-sm text-slate-600">
              {selectedLead ? <span className="font-medium text-[#0066CC]">{selectedLead.name}</span> : <span className="text-slate-400">No lead selected</span>}
              <span className="mx-2 text-slate-300">|</span>
              {selectedProducts.length > 0 ? <span className="font-medium text-[#0066CC]">{selectedProducts.length} product(s)</span> : <span className="text-slate-400">No products selected</span>}
            </div>
            <Button onClick={generateLinks} disabled={!selectedLead || selectedProducts.length === 0} className="bg-[#0066CC] gap-1.5 w-full sm:w-auto" data-testid="generate-demo-links">
              <Link2 className="w-4 h-4" /> Generate Demo Links
            </Button>
          </div>
        </div>
      )}

      {/* Generated Result */}
      {generated && (
        <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-4" data-testid="generated-demo-links">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-4">
            <div>
              <h4 className="font-semibold text-emerald-900">Demo Package Ready</h4>
              <p className="text-xs text-emerald-700 mt-0.5">For {generated.lead_name} {generated.lead_company && `— ${generated.lead_company}`}</p>
            </div>
            <Button variant="outline" size="sm" onClick={copyAll} className="gap-1 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-100 w-full sm:w-auto"><Copy className="w-3 h-3" /> Copy All</Button>
          </div>
          <div className="space-y-3">
            {(generated.products || []).map(p => (
              <div key={p.id} className="bg-white rounded-xl p-4 border border-emerald-100 shadow-sm">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-[#0066CC]/10 flex items-center justify-center"><Globe className="w-4 h-4 text-[#0066CC]" /></div>
                    <p className="font-semibold text-sm text-slate-900">{p.name}</p>
                  </div>
                  {p.demo_url && <a href={p.demo_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 flex items-center gap-1 hover:underline bg-blue-50 px-2.5 py-1 rounded-lg"><ExternalLink className="w-3 h-3" /><span className="truncate max-w-[200px]">{p.demo_url}</span></a>}
                </div>
                {p.credentials && Object.values(p.credentials).some(c => c?.username) && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {Object.entries(p.credentials).map(([role, creds]) => creds?.username && (
                      <div key={role} className="bg-slate-50 rounded-lg p-2.5 border border-slate-100">
                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">{role}</p>
                        <p className="text-xs"><span className="text-slate-400 mr-1">User:</span><span className="font-mono font-medium text-slate-700">{creds.username}</span></p>
                        <p className="text-xs mt-0.5"><span className="text-slate-400 mr-1">Pass:</span><span className="font-mono font-medium text-slate-700">{creds.password}</span></p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ============ SHARED COMPONENTS ============ */
function LoadingState() { return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-slate-300" /></div>; }
function EmptyState({ icon: Icon, text }) { return <div className="text-center py-16"><Icon className="w-12 h-12 text-slate-200 mx-auto mb-3" /><p className="text-slate-400 text-sm">{text}</p></div>; }
